import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Video, MessageSquare, Award, CheckCircle2, Sparkles, Users, Trophy, Zap, Target, Heart, Star } from "lucide-react";
import { api, formatIDR } from "@/lib/api";

const LOGO_TAG = "https://customer-assets.emergentagent.com/job_belajar-usaha/artifacts/40t1ulzr_logo%20warna%202.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const toAbsUrl = (url) => url && !url.startsWith("http") ? `${BACKEND_URL}${url}` : url;

const ICON_MAP = { BookOpen, Video, MessageSquare, Award, Users, Trophy, Zap, Target, Heart, Star, Sparkles };

export default function LandingPage() {
  const [courses, setCourses] = useState([]);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/landing-config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  if (!cfg) {
    return <div className="min-h-screen bg-white" />;
  }

  const features = cfg.features || [];

  return (
    <div className="bg-white" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#6B21A8] opacity-25 blur-3xl" />
        <div className="absolute top-40 -left-40 w-[400px] h-[400px] rounded-full bg-[#FF8C00] opacity-15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-28 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-[#FAF9FC] border border-[#E8E5EE] rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#FF8C00]" />
              <span className="text-xs text-[#5C5868]">{cfg.hero_eyebrow}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A1422] leading-[1.05] mb-6">
              {cfg.hero_title_part1}<br />{cfg.hero_title_part2}<br />
              <span className="text-[#FF8C00]">{cfg.hero_title_highlight}</span> {cfg.hero_title_part3}
            </h1>
            <p className="text-base sm:text-lg text-[#5C5868] mb-8 max-w-lg whitespace-pre-line">
              {cfg.hero_subtitle}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/login" className="btn-primary" data-testid="hero-cta-start">
                {cfg.hero_cta_primary} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#kursus" className="btn-outline" data-testid="hero-cta-view-courses">
                {cfg.hero_cta_secondary}
              </a>
            </div>
            {cfg.hero_bullets?.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 text-sm text-[#5C5868]">
                {cfg.hero_bullets.map((b, i) => (
                  <div key={i} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#FF8C00]" /> {b}</div>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-[#FF8C00]/30 to-[#6B21A8]/40 blur-2xl" />
            <img src={toAbsUrl(cfg.hero_image)} alt="Wirausaha belajar online" className="relative rounded-3xl border border-[#E8E5EE] w-full h-[420px] object-cover" />
            {cfg.hero_badge_value && (
              <div className="absolute -bottom-6 -left-6 card-surface p-4 max-w-[200px] glow-orange hidden sm:block">
                <div className="text-3xl font-heading font-extrabold text-[#FF8C00]">{cfg.hero_badge_value}</div>
                <div className="text-xs text-[#5C5868]">{cfg.hero_badge_text}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      {features.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => {
              const Icon = ICON_MAP[f.icon] || BookOpen;
              return (
                <div key={i} className="card-surface p-6 card-hover" data-testid={`feature-card-${i}`}>
                  <div className="w-12 h-12 rounded-2xl bg-[#FF8C00]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#FF8C00]" />
                  </div>
                  <h3 className="font-heading font-bold text-[#1A1422] text-lg mb-1">{f.title}</h3>
                  <p className="text-sm text-[#5C5868]">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* COURSES PREVIEW */}
      <section id="kursus" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A1422]">{cfg.courses_title}</h2>
            <p className="text-[#5C5868] mt-2">{cfg.courses_subtitle}</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.length === 0 && (
            <div className="col-span-full card-surface p-10 text-center text-[#5C5868]">
              Belum ada kursus dipublikasikan.
            </div>
          )}
          {courses.map((c) => (
            <div key={c.course_id} className="card-surface overflow-hidden card-hover" data-testid={`course-card-${c.course_id}`}>
              <div className="h-44 bg-[#F4F2F8] relative overflow-hidden">
                {c.thumbnail && <img src={toAbsUrl(c.thumbnail)} alt={c.title} className="w-full h-full object-cover" />}
                <span className="absolute top-3 left-3 bg-black/60 backdrop-blur text-[#FF8C00] text-xs font-semibold px-3 py-1 rounded-full border border-[#FF8C00]/30">
                  {c.category}
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-heading font-bold text-[#1A1422] text-lg mb-2 line-clamp-2">{c.title}</h3>
                <p className="text-sm text-[#5C5868] line-clamp-2 mb-4">{c.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[#FF8C00] font-heading font-bold text-lg">{formatIDR(c.price)}</span>
                  <Link to="/login" className="text-sm text-[#1A1422] hover:text-[#FF8C00] flex items-center gap-1" data-testid={`course-link-${c.course_id}`}>
                    Lihat <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="card-surface p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-[#FF8C00] opacity-10 blur-3xl pointer-events-none" />
          <img src={LOGO_TAG} alt="BisaUsaha" className="mx-auto h-20 mb-4 relative z-10" />
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A1422] mb-3 relative z-10">{cfg.cta_title}</h2>
          <p className="text-[#5C5868] mb-6 max-w-xl mx-auto relative z-10">{cfg.cta_subtitle}</p>
          <a href="/login" className="btn-primary inline-flex relative z-10" data-testid="bottom-cta-button" style={{touchAction:"manipulation"}}>
            {cfg.cta_button} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <footer className="border-t border-[#E8E5EE] py-8 text-center text-sm text-[#8B8794]">
        {(cfg.footer_text || "").replace("{year}", new Date().getFullYear())}
      </footer>
    </div>
  );
}
